import {
  GigyaData,
  GigyaProfile,
  GigyaResponseType,
  Hash,
  LoginUserType,
  RegisterUserType,
  UserInformation,
  UserResponseType,
} from './types';
import { isEmpty } from 'lodash-es';
import { api, axiosInstance } from '@xarvio/frontend-core-platform/lib/api';
import {
  getGigyaUrl,
  getPublicUrl,
} from '@xarvio/frontend-core-platform/lib/url';
import axios, { AxiosRequestConfig } from 'axios';
import cookieReader from 'js-cookie';
import { getGigyaErrorMessage } from './utils';
import {
  DOC_PATH_PREFIX,
  resolveDocHashKey,
  resolveDocUrl,
} from '../docs/resolveDocUrl';
import { getPublicDocsConfig } from '../docs/getPublicDocsConfig';
import type { WhitelabelDocsConfig } from '../docs/types';

export const getUserDetails = (userId: string) => {
  return api.get<void, UserInformation[]>(`/users/users?uuid=${userId}`);
};

export const getToken = ({
  UID,
  UIDSignature,
  signatureTimestamp,
}: Pick<GigyaResponseType, 'UID' | 'UIDSignature' | 'signatureTimestamp'>) => {
  const params = {
    gigyaUuid: UID,
    gigyaUuidSignature: UIDSignature,
    gigyaSignatureTimestamp: signatureTimestamp,
  };
  return api.post<void, UserResponseType>(
    '/users/tokens',
    JSON.stringify(params),
  );
};

const login = async ({
  email,
  password,
}: LoginUserType): Promise<GigyaResponseType> => {
  const params = {
    include: 'emails,profile,data',
    loginID: email?.trim(),
    password: password?.trim(),
    apiKey: import.meta.env.VITE_GIGYA_API_KEY,
  };

  const response = await axios.get<GigyaResponseType>(
    `${getGigyaUrl()}/accounts.login`,
    {
      params,
    },
  );

  return response.data;
};

export const requestTermsHashes = async (
  docsConfig: WhitelabelDocsConfig | undefined = getPublicDocsConfig(),
) => {
  try {
    const hashJson = await axiosInstance.get<void, Hash>(
      `${getPublicUrl()}${DOC_PATH_PREFIX}/hash.json`,
    );
    return hashJson[resolveDocHashKey('tc', docsConfig)];
  } catch {
    return undefined;
  }
};

const register = async (user: RegisterUserType, recaptchaToken: string) => {
  const config: AxiosRequestConfig = {
    headers: {
      'g-recaptcha-response': recaptchaToken,
      IGNORE_HTTP_ERROR: ['500'],
    },
  };

  const tcSignup = await requestTermsHashes();

  if (isEmpty(tcSignup) || !tcSignup) {
    return undefined;
  }
  const tacHash = tcSignup[`${user.language}_${user.country}`];

  const userData = {
    ...user,
    tacHash,
  };

  const payload = JSON.stringify(userData);

  return api.post('/users/users', payload, config);
};

const fetchDoc = (
  type: 'tc' | 'privacy',
  locale: string,
  docsConfig: WhitelabelDocsConfig | undefined,
) =>
  axiosInstance.get<void, string>(
    `${getPublicUrl()}${resolveDocUrl(type, locale, docsConfig)}`,
  );

export const requestTermsAndService = (
  locale: string,
  docsConfig: WhitelabelDocsConfig | undefined = getPublicDocsConfig(),
) => fetchDoc('tc', locale, docsConfig);

export const requestPrivacyPolicy = (
  locale: string,
  docsConfig: WhitelabelDocsConfig | undefined = getPublicDocsConfig(),
) => fetchDoc('privacy', locale, docsConfig);

type ForgotPasswordProps = {
  email: string;
  country: string;
  language: string;
};

export const forgotPassword = (userData: ForgotPasswordProps) => {
  return api.post<void, ForgotPasswordProps>(
    '/users/passwords',
    JSON.stringify(userData),
  );
};

type ResetPasswordProps = {
  email: string;
  password: string;
  passwordToken: string;
};

export const resetPassword = async (userData: ResetPasswordProps) => {
  const params = {
    loginID: userData.email,
    newPassword: userData.password,
    passwordResetToken: userData.passwordToken,
    apiKey: import.meta.env.VITE_GIGYA_API_KEY,
  };

  const response = await axios.get<GigyaResponseType>(
    `${getGigyaUrl()}/accounts.resetPassword`,
    {
      params,
    },
  );

  return response.data;
};

const inFlightAccountInfo = new Map<string, Promise<GigyaResponseType>>();

export const getAccountInfo = (userUuid: string) => {
  const loginToken =
    cookieReader.get(import.meta.env.VITE_GIGYA_LOGIN_TOKEN_NAME) ?? '';
  const requestKey = `${userUuid}:${loginToken}`;
  const inFlight = inFlightAccountInfo.get(requestKey);

  if (inFlight) {
    return inFlight;
  }

  const params = {
    extraProfileFields: 'address,phones,languages',
    include: 'profile,data,emails',
    UID: userUuid,
    apiKey: import.meta.env.VITE_GIGYA_API_KEY,
    login_token: loginToken,
  };

  const request = axios
    .get<GigyaResponseType>(`${getGigyaUrl()}/accounts.getAccountInfo`, {
      params,
    })
    .then((response) => {
      const responseData = response.data;

      const error = getGigyaErrorMessage(responseData);

      if (error) {
        throw error;
      }

      return responseData;
    })
    .finally(() => {
      inFlightAccountInfo.delete(requestKey);
    });

  inFlightAccountInfo.set(requestKey, request);

  return request;
};

export type VerifyPasswordProps = {
  email: string;
  password: string;
};

export const verifyPassword = async ({
  email,
  password,
}: VerifyPasswordProps) => {
  const params = {
    loginID: email,
    password,
    loginMode: 'reAuth',
    apiKey: import.meta.env.VITE_GIGYA_API_KEY,
  };

  const response = await axios.get<GigyaResponseType>(
    `${getGigyaUrl()}/accounts.login`,
    {
      params,
    },
  );

  return response.data;
};

type UpdateUserEmailProps = {
  uuid: string;
  email: string;
};

export const updateUserEmail = ({ uuid, email }: UpdateUserEmailProps) => {
  const params = {
    login: email,
  };

  return api.post(`users/users/${uuid}/accounts`, params);
};

export type UpdatePasswordPayload = {
  password: string;
  newPassword: string;
};

type UpdateUserPasswordProps = {
  uuid: string;
  payload: UpdatePasswordPayload;
};

export const updateUserPassword = ({
  uuid,
  payload,
}: UpdateUserPasswordProps) => {
  return api.put(`users/users/${uuid}/password`, payload);
};

const updateUser = async (
  userId: string,
  user: Partial<GigyaProfile>,
  data?: Partial<GigyaData>,
) => {
  const params: Record<string, string> = {
    UID: userId,
    login_token:
      cookieReader.get(import.meta.env.VITE_GIGYA_LOGIN_TOKEN_NAME) || '',
    apiKey: import.meta.env.VITE_GIGYA_API_KEY,
    profile: JSON.stringify(user),
    ...(data ? { data: JSON.stringify(data) } : {}),
  };

  // Gigya POST accepts application/x-www-form-urlencoded: https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/416da81f70b21014bbc5a10ce4041860.html
  const response = await axios.post(
    `${getGigyaUrl()}/accounts.setAccountInfo`,
    new URLSearchParams(params).toString(),
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    },
  );

  // Gigya always returns 200, so throw on error payloads to give user feedback.
  const responseData = response.data;
  const error = getGigyaErrorMessage(responseData);
  if (error) {
    throw error;
  }

  return responseData;
};

export const verifyUser = () => {
  return api.post(`users/user-verification`);
};

export const deleteUser = (userId: string) => {
  return api.delete(`users/users/${userId}`);
};

export const authService = {
  login,
  register,
  getToken,
  resetPassword,
  forgotPassword,
  updateUser,
  verifyPassword,
  updateUserPassword,
  updateUserEmail,
  verifyUser,
  deleteUser,
  getAccountInfo,
  getUserDetails,
};
